export interface EdgeDetectorOptions {
  targetWidth?: number;
  targetHeight?: number;
  threshold?: number;
}

export class EdgeDetector {
  private readonly work: OffscreenCanvas;
  private readonly w: number;
  private readonly h: number;
  private readonly threshold: number;
  private grayBuf: Uint8ClampedArray;
  private lastEdges: ImageData;

  constructor(opts: EdgeDetectorOptions = {}) {
    this.w = opts.targetWidth ?? 320;
    this.h = opts.targetHeight ?? 180;
    this.threshold = opts.threshold ?? 60;
    this.work = new OffscreenCanvas(this.w, this.h);
    this.grayBuf = new Uint8ClampedArray(this.w * this.h);
    this.lastEdges = new ImageData(this.w, this.h);
  }

  width(): number {
    return this.w;
  }

  height(): number {
    return this.h;
  }

  detect(source: HTMLVideoElement | HTMLCanvasElement): ImageData | null {
    const ctx = this.work.getContext('2d');
    if (!ctx) return null;
    const srcW = 'videoWidth' in source ? source.videoWidth : source.width;
    const srcH = 'videoHeight' in source ? source.videoHeight : source.height;
    if (srcW <= 0 || srcH <= 0) return null;
    ctx.drawImage(source, 0, 0, srcW, srcH, 0, 0, this.w, this.h);
    const src = ctx.getImageData(0, 0, this.w, this.h);
    for (let i = 0; i < this.w * this.h; i += 1) {
      const r = src.data[i * 4];
      const g = src.data[i * 4 + 1];
      const b = src.data[i * 4 + 2];
      this.grayBuf[i] = (r + g + b) / 3;
    }
    const out = this.lastEdges.data;
    out.fill(0);
    for (let y = 1; y < this.h - 1; y += 1) {
      for (let x = 1; x < this.w - 1; x += 1) {
        const i00 = (y - 1) * this.w + (x - 1);
        const i01 = (y - 1) * this.w + x;
        const i02 = (y - 1) * this.w + (x + 1);
        const i10 = y * this.w + (x - 1);
        const i12 = y * this.w + (x + 1);
        const i20 = (y + 1) * this.w + (x - 1);
        const i21 = (y + 1) * this.w + x;
        const i22 = (y + 1) * this.w + (x + 1);
        const gx =
          this.grayBuf[i02] +
          2 * this.grayBuf[i12] +
          this.grayBuf[i22] -
          this.grayBuf[i00] -
          2 * this.grayBuf[i10] -
          this.grayBuf[i20];
        const gy =
          this.grayBuf[i20] +
          2 * this.grayBuf[i21] +
          this.grayBuf[i22] -
          this.grayBuf[i00] -
          2 * this.grayBuf[i01] -
          this.grayBuf[i02];
        const mag = Math.min(255, Math.hypot(gx, gy));
        if (mag < this.threshold) continue;
        const dst = (y * this.w + x) * 4;
        out[dst] = 255;
        out[dst + 1] = 230;
        out[dst + 2] = 120;
        out[dst + 3] = Math.min(200, mag);
      }
    }
    return this.lastEdges;
  }
}
